import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { useUserDevice } from '@/hooks/useUserDevice';
import { useUser } from '@clerk/clerk-react';

// Helper to get serial from header injection (window variable or meta tag)
const getSerialFromHeader = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  
  // Check window variable (injected by server/native app)
  const windowSerial = (window as any).__DEVICE_SERIAL__;
  if (windowSerial) return windowSerial;
  
  // Check meta tag (injected by server/proxy)
  const metaTag = document.querySelector('meta[name="x-device-serial"]');
  if (metaTag) {
    const content = metaTag.getAttribute('content');
    if (content) return content;
  }
  
  return undefined;
};

// Hardware serials are uppercase alphanumeric (e.g., F4GDX1WJ0GQJ)
// Convex IDs are typically lowercase with different patterns
const HARDWARE_SERIAL_PATTERN = /^[A-Z0-9]{8,20}$/;

export const useEffectiveSerial = (): { serial: string | null; source: string; isLoading: boolean } => {
  const { user: clerkUser } = useUser();
  const { selectedDevice } = useDevice();
  const { device: convexDevice, isLoading: isUserDeviceLoading } = useUserDevice();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Validate serial - filter out route paths, invalid values, and ensure hardware serial format
  const isValidSerial = (value: string | undefined): boolean => {
    if (!value) return false;
    const invalidSerials = ['updates', 'settings', 'support', 'requests', 'general', 'device', 'device-selector'];
    if (invalidSerials.includes(value.toLowerCase())) return false;
    // Must match hardware serial pattern (uppercase alphanumeric, 8-20 chars)
    return HARDWARE_SERIAL_PATTERN.test(value);
  };

  // Get the hardware serial from the Convex device (useUserDevice now includes it)
  const serialFromConvexDevice = convexDevice?.serial || undefined;

  const serialFromHeader = getSerialFromHeader();
  const serialFromQuery = searchParams.get('cx__serial') || undefined;
  const serialFromPath = params.deviceId || undefined;
  const serialFromContext = selectedDevice?.serial || undefined;
  const serialFromStorage = typeof window !== 'undefined' ? localStorage.getItem('device_serial') || undefined : undefined;

  const resolved = useMemo(() => {
    // Priority: header > query > convex device > path > context > localStorage > null (NO default!)
    const serial = 
      (isValidSerial(serialFromHeader) ? serialFromHeader : undefined) ||
      (isValidSerial(serialFromQuery) ? serialFromQuery : undefined) ||
      (isValidSerial(serialFromConvexDevice) ? serialFromConvexDevice : undefined) ||
      (isValidSerial(serialFromPath) ? serialFromPath : undefined) ||
      (isValidSerial(serialFromContext) ? serialFromContext : undefined) ||
      (isValidSerial(serialFromStorage) ? serialFromStorage : undefined) ||
      null; // NO DEFAULT FALLBACK - return null if no valid serial found

    const source =
      (isValidSerial(serialFromHeader) && 'header') ||
      (isValidSerial(serialFromQuery) && 'query') ||
      (isValidSerial(serialFromConvexDevice) && 'convex') ||
      (isValidSerial(serialFromPath) && 'path') ||
      (isValidSerial(serialFromContext) && 'context') ||
      (isValidSerial(serialFromStorage) && 'localStorage') ||
      'none';

    // Loading if: user device is loading (still resolving Convex device)
    const isLoading = isUserDeviceLoading;

    return { serial, source, isLoading };
  }, [serialFromHeader, serialFromQuery, serialFromConvexDevice, serialFromPath, serialFromContext, serialFromStorage, isUserDeviceLoading]);

  useEffect(() => {
    if (typeof window !== 'undefined' && resolved.serial && isValidSerial(resolved.serial)) {
      localStorage.setItem('device_serial', resolved.serial);
    }
  }, [resolved.serial]);

  return resolved;
};
